import { useEffect, useState } from 'react';
import { isBlockingDialogOpen } from '../lib/coachDefer';

/**
 * useBlockingDialogOpen — true while a real modal (`role="dialog"
 * aria-modal="true"`, i.e. anything built on ui/Modal) is on the page.
 *
 * The guided tour uses it to PAUSE a run, not just to avoid starting one:
 * a tour can begin the instant a page's anchors mount, and a modal that
 * opens a beat later (the picked-template modal on /templates after
 * onboarding, a confirm dialog, the connectors overview) would otherwise sit
 * underneath a floating card and a ring drawn around something it covers.
 * The modal wins; the tour comes back where it was when the modal closes.
 *
 * Only observes while `active` — there is nothing to pause otherwise.
 * MutationObserver on the body catches mount/unmount and the aria-modal
 * attribute; a slow interval is the fallback for anything an observer might
 * miss (portals that toggle visibility without touching attributes).
 */
export function useBlockingDialogOpen(active: boolean, pollMs: number = 400): boolean {
  const [open, setOpen] = useState<boolean>(() => (active ? isBlockingDialogOpen() : false));

  useEffect(() => {
    if (!active) {
      setOpen(false);
      return;
    }
    if (typeof document === 'undefined') return;

    const check = () => setOpen(isBlockingDialogOpen());
    check();

    let observer: MutationObserver | null = null;
    if (typeof MutationObserver !== 'undefined') {
      observer = new MutationObserver(check);
      observer.observe(document.body, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['aria-modal', 'role'],
      });
    }
    const interval = setInterval(check, pollMs);

    return () => {
      observer?.disconnect();
      clearInterval(interval);
    };
  }, [active, pollMs]);

  return active && open;
}

export default useBlockingDialogOpen;
